import React, { useState, useEffect } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row, Table, InputGroup, CardTitle } from 'reactstrap';
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs";
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../Service/crypto';
import { Edit, PlusCircle, Trash2 } from 'react-feather';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../api';
import websocket_webservice from '../../../Service/websocket_webservice';

const FieldMaster: React.FC = () => {
    const [decryptedData, setDecryptedData] = useState<any>({});
    const [currentField, setCurrentField] = useState<any>(null);
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newField, setNewField] = useState<any>({ index: '', value: '', instruction: '' });
    const [query, setQuery] = useState<string>('');
    const [currentCategory, setCurrentCategory] = useState<string | null>(null);
    const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({});
    const [expandAll, setExpandAll] = useState<boolean>(false);
    const [isEditCardModalOpen, setIsEditCardModalOpen] = useState(false);
    const [oldCategoryName, setOldCategoryName] = useState<string>('');
    const [newCategoryName, setNewCategoryName] = useState<string>('');
    const UserId = localStorage.id;
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [bulkPasteText, setBulkPasteText] = useState('');
    const [parsedBulkRows, setParsedBulkRows] = useState<Array<{ sectionName: string; fieldName: string; instruction: string }>>([]);

    const fetchData = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, UserId);
        try {
            const response = await api.fetch("api/TemplateCreation/get_patient_details", {
                method: 'POST',
                body: JSON.stringify({ EncParams }),
            });
            if (!response.ok) {
                throw new Error('Failed to fetch data');
            }
            const result = await response.json();
            const decryptedResult = await AesDecrypt(key, result);
            if (decryptedResult.message === 'There is no Record Found') {
                setDecryptedData({});
            } else {
                const filteredData = Object.fromEntries(
                    Object.entries(decryptedResult[0])
                        .filter(([key]) => key !== '_id')
                );
                setDecryptedData(filteredData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchData();
        const socket = new WebSocket(`${websocket_webservice}ws/TempalateField/`);
        socket.onmessage = () => {
            fetchData();
        };
        return () => {
            if (socket.readyState === 1) {
                socket.close();
            }
        };
    }, []);

    const toggleEditModal = () => {
        setIsEditModalOpen(!isEditModalOpen);
    };

    const toggleEditCardModal = () => {
        setIsEditCardModalOpen(!isEditCardModalOpen);
    };

    const handleEditCard = (category: string) => {
        setOldCategoryName(category);
        setNewCategoryName(category);
        toggleEditCardModal();
    };

    const handleSaveEditCard = async () => {
        if (!newCategoryName.trim() || newCategoryName === oldCategoryName) {
            toggleEditCardModal();
            return;
        }

        const key = await keyPromise;
        const EncParams = await AesEncrypt(key, { old_category: oldCategoryName, new_category: newCategoryName });
        const Username = localStorage.UserName;
        const Data = { old_category: oldCategoryName, new_category: newCategoryName, Username };

        try {
            const response = await api.fetch('api/TemplateField/EditCard', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });

            await api.fetch('api/TemplateField/EditSectionLog', {
                method: 'POST',
                body: JSON.stringify(Data)
            });

            toggleEditCardModal();

            if (response.status === 200) {
                const resData = await response.json();
                if (resData === "Section already exists") {
                    Swal.fire({
                        icon: "error",
                        title: "Insurance Verification Platform",
                        text: "Section name already exists.",
                        customClass: {
                            confirmButton: "btn btn-danger",
                        },
                        allowOutsideClick: false
                    });
                } else {
                    fetchData();
                    Swal.fire({
                        icon: "success",
                        title: "Insurance Verification Platform",
                        text: "Section Renamed Successfully.",
                        customClass: {
                            confirmButton: "btn btn-success",
                        },
                        allowOutsideClick: false
                    });
                }
            }
        } catch (error) {
            console.error("Error editing section name:", error);
            toggleEditCardModal();
        }
    };

    const toggleCardModalOpen = () => {
        setIsCardModalOpen(!isCardModalOpen);
    };

    const toggleAddModal = () => {
        if (isAddModalOpen) {
            setIsBulkMode(false);
            setBulkPasteText('');
            setParsedBulkRows([]);
        }
        setIsAddModalOpen(!isAddModalOpen);
    };

    const handleAddCard = (category: string) => {
        setCurrentCategory(category);
        setNewField({ index: category, value: '', instruction: '' });
        toggleCardModalOpen();
    };

    const handleEdit = (category: string, index: string) => {
        setCurrentField({ category, index });
        const fieldValue = decryptedData[category][index];
        if (typeof fieldValue === 'object') {
            setNewField({ index: index, value: fieldValue.field_name, instruction: fieldValue.instruction || '' });
        } else {
            setNewField({ index: index, value: fieldValue, instruction: '' });
        }
        toggleEditModal();
    };

    const handleAdd = (category: string) => {
        setCurrentCategory(category);
        setNewField({ index: '', value: '', instruction: '' });
        toggleAddModal();
    };

    // Delete Keys 
    const handleDelete = async (category: string, index: string) => {
        const userConfirmation = await Swal.fire({
            title: "Insurance Verification Platform",
            text: "Are you sure you want to delete?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            customClass: {
                confirmButton: "btn btn-primary",
                cancelButton: "btn btn-danger ms-1",
            },
            buttonsStyling: false,
            allowOutsideClick: false
        });

        if (userConfirmation.isConfirmed) {
            const updatedCategoryData = { ...decryptedData[category] };
            const deletedValue = updatedCategoryData[index];
            delete updatedCategoryData[index];
            setDecryptedData({ ...decryptedData, [category]: updatedCategoryData });
            const Username = localStorage.UserName;
            const Data = { deletedValue, Username };
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, { category, index });
            const response = await api.fetch('api/TemplateField/Delete', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            })
            await api.fetch('api/TemplateField/DeleteSectionDataLog', {
                method: 'POST',
                body: JSON.stringify(Data)
            })

            if (response.status === 200) {
                fetchData();
                Swal.fire({
                    icon: "success",
                    title: "Insurance Verification Platform",
                    text: "Field Deleted Successfully.",
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                    allowOutsideClick: false
                });
            }
        }

    };

    // Delete Card Keys 
    const handleDeleteCard = async (category: string) => {
        const userConfirmation = await Swal.fire({
            title: "Insurance Verification Platform",
            text: "Are you sure you want to delete?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            customClass: {
                confirmButton: "btn btn-primary",
                cancelButton: "btn btn-danger ms-1",
            },
            buttonsStyling: false,
            allowOutsideClick: false
        });

        if (userConfirmation.isConfirmed) {
            const Username = localStorage.UserName;
            const Data = { category, Username };
            // Update the state by deleting the card category
            const updatedData = { ...decryptedData };
            delete updatedData[category];
            setDecryptedData(updatedData);
            const key = await keyPromise
            // Encrypt the category for the API call
            const EncParams = await AesEncrypt(key, { category });
            // Make the API call to delete the card
            const response = await api.fetch('api/TemplateField/DeleteCard', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });
            await api.fetch('api/TemplateField/DeleteSectionLog', {
                method: 'POST',
                body: JSON.stringify(Data)
            });
            if (response.status === 200) {
                // Show success message and toast notification if deletion is successful
                fetchData();
                Swal.fire({
                    icon: "success",
                    title: "Insurance Verification Platform",
                    text: "Section Deleted Successfully.",
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                    allowOutsideClick: false
                });
            }
        }
    };

    // Edit Keys
    const handleSaveEdit = async () => {

        let oldValue = '';
        let newValue = '';
        if (currentField) {
            // Capture old value before update
            const oldCategoryData = decryptedData[currentField.category];
            oldValue = oldCategoryData[currentField.index];

            // Prepare updated category data
            const updatedCategoryData = { ...oldCategoryData, [newField.index]: newField.value };
            newValue = updatedCategoryData[currentField.index];

            // Update the state with the new data
            setDecryptedData({ ...decryptedData, [currentField.category]: updatedCategoryData });
        }

        // Close the edit modal
        toggleEditModal();

        // Prepare audit log data
        const auditLogData = [
            {
                field: currentField.category,
                before: oldValue,
                after: newValue,
            },
        ];


        const Username = localStorage.UserName;
        const Data = { auditLogData, Username };
        const key = await keyPromise
        // Encrypt the current and new field data
        const EncParams = await AesEncrypt(key, { currentField, newField });
        const response = await api.fetch('api/TemplateField/EditData', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());
        await api.fetch('api/TemplateField/EditFieldLog', {
            method: 'POST',
            body: JSON.stringify({ Data })
        }).then((response) => response.json());

        if (response === 200) {
            fetchData();
            Swal.fire({
                title: "Insurance Verification Platform",
                html: '<span style="color: green;">Field Edited Successfully</span>',
                icon: "success",
                customClass: {},
                showClass: {
                    popup: "animate__animated animate__flipInX",
                },
                buttonsStyling: true,
                allowOutsideClick: false,
            })
        }
    };



    // Add section Keys 
    const handleSaveAdd = async () => {
        if (newField.index && newField.value) {
            if (currentCategory) {
                const updatedCategoryData = { ...decryptedData[currentCategory], [newField.index]: { field_name: newField.value, instruction: newField.instruction } };
                setDecryptedData({ ...decryptedData, [currentCategory]: updatedCategoryData });
            } else {
                setDecryptedData({ ...decryptedData, [newField.index]: [{ field_name: newField.value, instruction: newField.instruction }] });
            }
        }
        toggleAddModal();
        const auditLogData = [
            {
                field: "Section Name",
                value: newField.index,
            },
            {
                field: "Field Name",
                value: newField.value,
            },
            {
                field: "Instruction",
                value: newField.instruction,
            },
        ];
        const Username = localStorage.UserName;
        const Data = { auditLogData, Username };
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, { currentCategory, newField });
        const response = await api.fetch('api/TemplateField/AddData', {
            method: 'POST',
            body: JSON.stringify({ EncParams }),
            headers: {
                'Content-type': 'application/json; charset=UTF-8',
            }
        }).then((response) => response.json());
        await api.fetch('api/TemplateField/AddSectionLog', {
            method: 'POST',
            body: JSON.stringify({ Data })
        }).then((response) => response.json());
        if (response === 200) {
            fetchData();
            Swal.fire({
                title: "Insurance Verification Platform",
                html: '<span style="color: green;">New Section Added Successfully</span>',
                icon: "success",
                customClass: {},
                showClass: {
                    popup: "animate__animated animate__flipInX",
                },
                buttonsStyling: true,
                allowOutsideClick: false,
            })
        }
    };

    // Add Card Keys 
    const handleSaveCard = async () => {
        if (newField.value && currentCategory) {
            const updatedCategoryData = [...(decryptedData[currentCategory] || []), { field_name: newField.value, instruction: newField.instruction }];
            setDecryptedData({ ...decryptedData, [currentCategory]: updatedCategoryData });
        }
        toggleCardModalOpen();
        const auditLogData = [
            {
                field: "Section Name",
                value: newField.index,
            },
            {
                field: "Field Name",
                value: newField.value,
            },
            {
                field: "Instruction",
                value: newField.instruction,
            },
        ];
        const Username = localStorage.UserName;
        const Data = { auditLogData, Username };
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, { currentCategory, newField });
        const response = await api.fetch('api/TemplateField/AddCardData', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());
        await api.fetch('api/TemplateField/AddSectionLog', {
            method: 'POST',
            body: JSON.stringify({ Data })
        }).then((response) => response.json());
        if (response === 200) {
            fetchData();
            Swal.fire({
                title: "Insurance Verification Platform",
                html: '<span style="color: green;">New Field Added Successfully</span>',
                icon: "success",
                customClass: {},
                showClass: {
                    popup: "animate__animated animate__flipInX",
                },
                buttonsStyling: true,
                allowOutsideClick: false,
            })
        }
    };

    const parseBulkPaste = (text: string) => {
        setBulkPasteText(text);
        const lines = text.trim().split('\n').filter(l => l.trim());
        const rows = lines.map(line => {
            const cols = line.split('\t');
            return {
                sectionName: (cols[0] || '').trim(),
                fieldName: (cols[1] || '').trim(),
                instruction: (cols[2] || '').trim(),
            };
        }).filter(r => r.sectionName || r.fieldName);
        setParsedBulkRows(rows);
    };

    const handleBulkSaveAdd = async () => {
        if (parsedBulkRows.length === 0) return;
        toggleAddModal();
        const key = await keyPromise;
        const Username = localStorage.UserName;
        let successCount = 0;
        const createdSections = new Set<string>();

        for (const row of parsedBulkRows) {
            const bulkField = { index: row.sectionName, value: row.fieldName, instruction: row.instruction };
            const sectionExists = Object.prototype.hasOwnProperty.call(decryptedData, row.sectionName) || createdSections.has(row.sectionName);

            let response;
            if (sectionExists) {
                const EncParams = await AesEncrypt(key, { currentCategory: row.sectionName, newField: bulkField });
                response = await api.fetch('api/TemplateField/AddCardData', {
                    method: 'POST',
                    body: JSON.stringify({ EncParams }),
                }).then(r => r.json());
            } else {
                const EncParams = await AesEncrypt(key, { currentCategory: null, newField: bulkField });
                response = await api.fetch('api/TemplateField/AddData', {
                    method: 'POST',
                    body: JSON.stringify({ EncParams }),
                    headers: { 'Content-type': 'application/json; charset=UTF-8' }
                }).then(r => r.json());
                createdSections.add(row.sectionName);
            }

            const auditLogData = [
                { field: "Section Name", value: row.sectionName },
                { field: "Field Name", value: row.fieldName },
                { field: "Instruction", value: row.instruction },
            ];
            await api.fetch('api/TemplateField/AddSectionLog', {
                method: 'POST',
                body: JSON.stringify({ auditLogData, Username })
            });
            if (response === 200) successCount++;
        }
        fetchData();
        Swal.fire({
            title: "Insurance Verification Platform",
            html: `<span style="color: green;">${successCount} of ${parsedBulkRows.length} Fields Added Successfully</span>`,
            icon: "success",
            buttonsStyling: true,
            allowOutsideClick: false,
        });
    };

    const filterData = (data: any, query: string) => {
        if (!query) return data;
        const q = query.toLowerCase();
        const filteredData: any = {};
        for (const category in data) {
            filteredData[category] = {};
            for (const field in data[category]) {
                const value = data[category][field];
                const fieldName = typeof value === 'object' ? (value.field_name || '') : (typeof value === 'string' ? value : '');
                const instruction = typeof value === 'object' ? (value.instruction || '') : '';
                if (
                    category.toLowerCase().includes(q) ||
                    field.toLowerCase().includes(q) ||
                    fieldName.toLowerCase().includes(q) ||
                    instruction.toLowerCase().includes(q)
                ) {
                    filteredData[category][field] = value;
                }
            }
            if (Object.keys(filteredData[category]).length === 0) {
                delete filteredData[category];
            }
        }
        return filteredData;
    };

    const toggleCard = (category: string) => {
        setExpandedCards((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const toggleExpandAll = () => {
        const newExpandAll = !expandAll;
        setExpandAll(newExpandAll);
        const newExpandedCards = Object.keys(decryptedData).reduce((acc, category) => {
            acc[category] = newExpandAll;
            return acc;
        }, {} as { [key: string]: boolean });
        setExpandedCards(newExpandedCards);
    };

    const renderDataCard = () => {
        const filteredData = filterData(decryptedData, query);
        const totalSections = Object.keys(filteredData).length;
        const totalFields = Object.values(filteredData).reduce((sum: number, fields: any) => sum + Object.keys(fields).length, 0);
        return (
            <>
                <Breadcrumbs mainTitle='Manage Label' title='Manage Label' />
                <Container fluid>
                    <Row>
                        <Col xs="6">
                            <div className="d-flex justify-content-start mb-3">
                                {/* <Button color="primary" onClick={() => window.history.back()}>Back</Button> */}
                                <Button color="success" onClick={() => handleAdd('')} className="ml-2">Add New Section Field</Button>
                                <span style={{ marginLeft: '10px' }}></span>
                                <Col xs="8" className="d-flex align-items-center mb-3" style={{ gap: '10px' }}>
                                    <span style={{ background: '#2c4358', color: 'white', borderRadius: '6px', padding: '4px 10px', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        Sections: {totalSections}
                                    </span>
                                    <span style={{ background: '#16c7db', color: 'white', borderRadius: '6px', padding: '4px 10px', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        Fields: {totalFields}
                                    </span>
                                </Col>
                            </div>
                        </Col>
                        <Col xs="2">
                            <div className="d-flex justify-content-end mb-3">
                                <Button color="primary" onClick={toggleExpandAll} style={{ cursor: "pointer" }}>
                                    {expandAll ? 'Collapse All' : 'Expand All'}
                                </Button>
                            </div>
                        </Col>
                        <Col
                            lg='4'
                            className='d-flex align-items-center justify-content-lg-end mt-lg-0 mt-1 px-0 px-lg-1'
                        >
                            <InputGroup className="input-group-search mb-2 zoom-on-hover">
                                <Input
                                    type="text"
                                    placeholder="🔍 Search fields..."
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                    </Row>
                    {Object.keys(filteredData).map((category, index) => (
                        <Card key={index} className="mouse_over-card" style={{ boxShadow: '0 0 10px #26394b' }}>
                            <CardHeader style={{ backgroundColor: '#2c4358', color: 'white', cursor: 'pointer' }}>
                                <Row>
                                    <Col xs="10" onClick={() => toggleCard(category)} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <CardTitle tag="h3" style={{ margin: 0 }}>{category}</CardTitle>
                                        <span style={{ background: '#16c7db', color: 'white', borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: 700, minWidth: '28px', textAlign: 'center' }}>
                                            {Object.keys(filteredData[category]).length}
                                        </span>
                                        <Edit
                                            size={18}
                                            style={{ cursor: "pointer", color: "#16c7db" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditCard(category);
                                            }}
                                        />
                                    </Col>
                                    <Col xs="1" className="ml-auto">
                                        <Button color="link" onClick={() => toggleCard(category)} className="float-right">
                                            {expandedCards[category] ? <FaChevronUp /> : <FaChevronDown />}
                                        </Button>
                                    </Col>
                                    <Col xs="1" className="ml-auto">
                                        <Trash2
                                            size={20}
                                            style={{ cursor: "pointer", color: "red", float: "right" }}
                                            onClick={() => handleDeleteCard(category)}
                                        />
                                        <PlusCircle
                                            size={20}
                                            color='white'
                                            style={{ cursor: "pointer", float: "right", marginRight: "1%" }}
                                            onClick={() => handleAddCard(category)}
                                        />
                                    </Col>
                                </Row>
                            </CardHeader>

                            {expandedCards[category] && (
                                <CardBody>
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th><strong>Field Name</strong></th>
                                                <th><strong>Instruction</strong></th>
                                                <th style={{ textAlign: "right" }}><strong>Action</strong></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(filteredData[category]).map(([field, value]: [any, any]) => (
                                                <tr key={field}>
                                                    <td>
                                                        <span title={typeof value === 'object' ? value.field_name : value} style={{ cursor: "pointer" }}>
                                                            {(typeof value === 'object' ? value.field_name : value).length > 160 ? `${(typeof value === 'object' ? value.field_name : value).substring(0, 30)}...` : (typeof value === 'object' ? value.field_name : value)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {typeof value === 'object' ? (
                                                            <span title={value.instruction} style={{ cursor: "pointer" }}>
                                                                {value.instruction.length > 160 ? `${value.instruction.substring(0, 160)}...` : value.instruction}
                                                            </span>
                                                        ) : ''}
                                                    </td>
                                                    <td style={{ display: "flex", justifyContent: "flex-end" }}>
                                                        <Edit size={20} style={{ cursor: "pointer", color: "#16c7db" }} onClick={() => handleEdit(category, field)} />
                                                        &nbsp;
                                                        &nbsp;
                                                        <Trash2 size={20} style={{ cursor: "pointer", color: "red" }} onClick={() => handleDelete(category, field)} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            )}
                        </Card>
                    ))}
                </Container>
                <Modal isOpen={isCardModalOpen} toggle={toggleCardModalOpen}>
                    <ModalHeader toggle={toggleCardModalOpen}>Add Field</ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label for="addFieldValue">Field Name</Label>
                                <Input
                                    type="textarea"
                                    id="addFieldValue"
                                    value={newField.value}
                                    onChange={(e) => setNewField({ ...newField, value: e.target.value })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="addFieldInstruction">Instruction</Label>
                                <Input
                                    type="textarea"
                                    id="addFieldInstruction"
                                    value={newField.instruction}
                                    onChange={(e) => setNewField({ ...newField, instruction: e.target.value })}
                                />
                            </FormGroup>
                            <Button color="primary" onClick={handleSaveCard}>Save Changes</Button>
                        </Form>
                    </ModalBody>
                </Modal>
                <Modal isOpen={isEditModalOpen} toggle={toggleEditModal}>
                    <ModalHeader toggle={toggleEditModal}>Edit Field</ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label for="editFieldValue">Field Name</Label>
                                <Input
                                    type="textarea"
                                    id="editFieldValue"
                                    value={newField.value}
                                    onChange={(e) => setNewField({ ...newField, value: e.target.value })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="editFieldInstruction">Instruction</Label>
                                <Input
                                    type="textarea"
                                    id="editFieldInstruction"
                                    value={newField.instruction}
                                    onChange={(e) => setNewField({ ...newField, instruction: e.target.value })}
                                />
                            </FormGroup>
                            <Button color="primary" onClick={handleSaveEdit}>Save Changes</Button>
                        </Form>
                    </ModalBody>
                </Modal>
                <Modal isOpen={isAddModalOpen} toggle={toggleAddModal} size={isBulkMode ? 'xl' : 'md'}>
                    <ModalHeader toggle={toggleAddModal}>Add Field</ModalHeader>
                    <ModalBody>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                            <Button
                                color="primary"
                                outline={isBulkMode}
                                size="sm"
                                onClick={() => setIsBulkMode(false)}
                            >Single Entry</Button>
                            <Button
                                color="primary"
                                outline={!isBulkMode}
                                size="sm"
                                onClick={() => setIsBulkMode(true)}
                            >Paste from Excel</Button>
                        </div>
                        {!isBulkMode ? (
                            <Form>
                                <FormGroup>
                                    <Label for="addFieldName">Section Name</Label>
                                    <Input
                                        type="text"
                                        id="addFieldName"
                                        value={newField.index}
                                        onChange={(e) => setNewField({ ...newField, index: e.target.value })}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="addFieldValue">Field Name</Label>
                                    <Input
                                        type="textarea"
                                        id="addFieldValue"
                                        value={newField.value}
                                        onChange={(e) => setNewField({ ...newField, value: e.target.value })}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="addFieldInstruction">Instruction</Label>
                                    <Input
                                        type="textarea"
                                        id="addFieldInstruction"
                                        value={newField.instruction}
                                        onChange={(e) => setNewField({ ...newField, instruction: e.target.value })}
                                    />
                                </FormGroup>
                                <Button color="primary" onClick={handleSaveAdd}>Add Field</Button>
                            </Form>
                        ) : (
                            <div>
                                <p style={{ color: '#555', fontSize: '13px', marginBottom: '10px', lineHeight: '1.6' }}>
                                    Copy rows from Excel and paste below. Expected columns:<br />
                                    <strong>Column A:</strong> Section Name &nbsp;|&nbsp;
                                    <strong>Column B:</strong> Field Name &nbsp;|&nbsp;
                                    <strong>Column C:</strong> Instruction (optional)
                                </p>
                                <Input
                                    type="textarea"
                                    rows={6}
                                    placeholder="Paste Excel data here (Ctrl+V)..."
                                    value={bulkPasteText}
                                    onChange={(e) => parseBulkPaste(e.target.value)}
                                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                                />
                                {parsedBulkRows.length > 0 && (
                                    <>
                                        <div style={{ marginTop: '14px', marginBottom: '8px', fontWeight: 'bold', color: '#2c4358' }}>
                                            Preview — {parsedBulkRows.length} row(s) detected:
                                        </div>
                                        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                            <Table size="sm" bordered style={{ margin: 0 }}>
                                                <thead style={{ backgroundColor: '#2c4358' }}>
                                                    <tr>
                                                        <th style={{ color: 'white' }}>#</th>
                                                        <th style={{ color: 'white' }}>Section Name</th>
                                                        <th style={{ color: 'white' }}>Field Name</th>
                                                        <th style={{ color: 'white' }}>Instruction</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {parsedBulkRows.map((row, i) => (
                                                        <tr key={i}>
                                                            <td>{i + 1}</td>
                                                            <td>{row.sectionName}</td>
                                                            <td>{row.fieldName}</td>
                                                            <td>{row.instruction}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <Button color="success" style={{ marginTop: '12px' }} onClick={handleBulkSaveAdd}>
                                            Add {parsedBulkRows.length} Field{parsedBulkRows.length > 1 ? 's' : ''}
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                    </ModalBody>
                </Modal>
                <Modal isOpen={isEditCardModalOpen} toggle={toggleEditCardModal}>
                    <ModalHeader toggle={toggleEditCardModal}>Edit Section Name</ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label for="editCardCategory">Section Name</Label>
                                <Input
                                    type="text"
                                    id="editCardCategory"
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                />
                            </FormGroup>
                            <Button color="primary" onClick={handleSaveEditCard}>Save Changes</Button>
                        </Form>
                    </ModalBody>
                </Modal>
            </>
        );
    };

    return renderDataCard();
};

export default FieldMaster;
